import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { SupabaseService } from './supabase.service';
import { LocalStorageUtils } from '../utils/user.utils';
import { ROUTES } from '../constants/app.constants';
import { DashboardUser } from '../models/user.model';
import { ToastService } from './toast.service';
import { SessionExpiryService } from './session-expiry.service';
import { RouteMetadataService } from './route-metadata.service';
import { FilterCacheService } from '../utils/filter-cache.service';
import { eKeyLocalStorage } from '../constants/options-system';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  private currentUserSubject = new BehaviorSubject<DashboardUser | null>(null);

  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  public currentUser$ = this.currentUserSubject.asObservable();
  constructor(
    private router: Router,
    private supabaseService: SupabaseService,
    private notificationService: ToastService,
    private sessionExpiryService: SessionExpiryService,
    private routeMetadataService: RouteMetadataService,
    private filterCache :FilterCacheService
  ) {
    this.initializeAuthState();
  }

  /**
   * Inicializa el estado de autenticación verificando la sesión actual
   */
  private async initializeAuthState(): Promise<void> {
    try {
      const { data: { session } } = await this.supabaseService.client.auth.getSession();

      if (session && session.access_token) {
        // Verificar si el token aún es válido
        const { data: { user }, error } = await this.supabaseService.client.auth.getUser();

        if (!error && user) {
          // Token válido, establecer estado autenticado
          const userData = LocalStorageUtils.getUserData(true) || LocalStorageUtils.getUserData(false);
          if (userData) {
            this.setAuthenticatedUser(userData);
          } else {
            // Si no hay datos en localStorage pero hay sesión, obtener datos del usuario
            await this.fetchAndSetUserData();
          }
        } else {
          // Token inválido, limpiar sesión
          await this.logout();
        }
      } else {
        // No hay sesión, usuario no autenticado
        this.setUnauthenticated();
      }
    } catch (error) {
      console.error('Error al inicializar estado de autenticación:', error);
      this.setUnauthenticated();
    }
  }

  /**
   * Verifica si el usuario está autenticado
   */
  async isUserAuthenticated(): Promise<boolean> {
    try {
      const { data: { session } } = await this.supabaseService.client.auth.getSession();

      if (!session || !session.access_token) {
        return false;
      }

      // Verificar si el token es válido
      const { data: { user }, error } = await this.supabaseService.client.auth.getUser();
      return !error && !!user;
    } catch (error) {
      console.error('Error al verificar autenticación:', error);
      return false;
    }
  }

  /**
   * Establece el usuario como autenticado
   */
  private setAuthenticatedUser(userData: DashboardUser): void {
    this.currentUserSubject.next(userData);
    this.isAuthenticatedSubject.next(true);
    this.sessionExpiryService.ensureSessionTracking();
    // El árbol de permisos por ruta depende de qué usuario está autenticado
    // (un mismo tab puede pasar de un tipo de usuario a otro sin recargar).
    this.routeMetadataService.clearCache();
  }

  /**
   * Establece el usuario como no autenticado
   */
  private setUnauthenticated(): void {
    this.currentUserSubject.next(null);
    this.isAuthenticatedSubject.next(false);
    this.sessionExpiryService.stopSessionTracking();
    this.routeMetadataService.clearCache();
    // Preservar el correo recordado ("Recordar mi cuenta") al limpiar el storage
    const rememberedEmail = localStorage.getItem(eKeyLocalStorage.RememberedEmail);
    localStorage.clear();
    if (rememberedEmail) {
      localStorage.setItem(eKeyLocalStorage.RememberedEmail, rememberedEmail);
    }
    // Limpiar datos locales
    // LocalStorageUtils.removeUserData(true);
    // LocalStorageUtils.removeUserData(false);
  }

  /**
   * Obtiene y establece los datos del usuario autenticado
   */
  private async fetchAndSetUserData(): Promise<void> {
    try {
      const userProfile = await this.supabaseService.getUserProfile();
      if (userProfile && userProfile.length > 0) {
        const userData = userProfile[0] as DashboardUser;
        const isCompany = userData.user_type_id === 2 || userData.user_type_id === 3;

        LocalStorageUtils.saveUserData(userData, isCompany);
        this.setAuthenticatedUser(userData);
      }
    } catch (error) {
      console.error('Error al obtener datos del usuario:', error);
      await this.logout();
    }
  }

  /**
   * Actualiza el estado de autenticación después de login exitoso
   */
  async updateAuthenticationState(userData?: DashboardUser): Promise<void> {
    if (userData) {
      const isCompany = userData.user_type_id === 2 || userData.user_type_id === 3;
      LocalStorageUtils.saveUserData(userData, isCompany);
      this.setAuthenticatedUser(userData);
    } else {
      await this.fetchAndSetUserData();
    }
  }

  /**
   * Igual que `updateAuthenticationState`, pero exclusivamente para el punto
   * en que el usuario acaba de autenticarse con credenciales (login real).
   * Arranca el reloj de idle/absoluto desde cero para que un login nuevo en el
   * mismo navegador no herede las marcas de tiempo de una sesión anterior que
   * no cerró sesión explícitamente (pestaña cerrada sin logout).
   */
  async completeLogin(userData?: DashboardUser): Promise<void> {
    this.sessionExpiryService.startNewSession();
    await this.updateAuthenticationState(userData);
  }

  /**
   * Cierra la sesión del usuario
   */
  async logout(): Promise<void> {
    try {
      await this.supabaseService.client.auth.signOut();
      this.filterCache.clearAll();
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    } finally {
      this.setUnauthenticated();
      this.redirectToLogin();
    }
  }

  /**
   * Redirige al login apropiado basado en la URL actual
   */
  redirectToLogin(): void {
    const currentUrl = this.router.url;

    // replaceUrl: evita apilar entradas de historial hacia login, que es justo lo
    // que hace fácil volver a aterrizar ahí con el botón "atrás" del navegador.
    if (currentUrl.includes('/dashboard/company') || currentUrl.includes('/auth/company')) {
      this.router.navigate([ROUTES.AUTH.COMPANY.LOGIN], { replaceUrl: true });
    } else if (currentUrl.includes('/dashboard/user') || currentUrl.includes('/auth/user')) {
      this.router.navigate([ROUTES.AUTH.USER.LOGIN], { replaceUrl: true });
    } else {
      // Default a company login
      this.router.navigate([ROUTES.AUTH.COMPANY.LOGIN], { replaceUrl: true });
    }
  }

  /**
   * Obtiene el usuario actual
   */
  getCurrentUser(): DashboardUser | null {
    return this.currentUserSubject.value;
  }

  /**
   * Verifica si el usuario actual es de tipo company
   */
  isCompanyUser(): boolean {
    const user = this.getCurrentUser();
    return user ? (user.user_type_id === 2 || user.user_type_id === 3) : false;
  }

  /**
   * Verifica si el usuario actual es de tipo user
   */
  isRegularUser(): boolean {
    const user = this.getCurrentUser();
    return user ? user.user_type_id === 1 : false;
  }
}
